import React, { useEffect, useRef, useState } from "react";
import { X, Loader2, Lock, CheckCircle2, AlertCircle, ShieldCheck } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { extractTokenId } from "@/lib/tapToken";
import { postDiagnostic } from "@/lib/tapDiagnostic";
import { useI18n } from "@/contexts/I18nContext";

// If Tap's SDK doesn't call onSuccess/onError within this window, we assume the
// iframe silently dropped the tokenize postMessage and recover the UI.
const TOKENIZE_TIMEOUT_MS = 20000;

/**
 * TapCardModal — collects card via Tap Card SDK v2, then calls the DAAM backend
 * to run a SAR 1 authorization (save_card=true). If 3DS is required, the parent
 * is redirected to Tap's transaction.url.
 *
 * Props:
 *   open        boolean
 *   plan        { id, name, name_ar, prices, currency }
 *   cycle       "monthly" | "yearly"
 *   onClose     () => void
 *   onSuccess   (data) => void   // called if authorize was AUTHORIZED without 3DS
 */
export default function TapCardModal({ open, plan, cycle, onClose, onSuccess }) {
  const { lang } = useI18n();
  const containerRef = useRef(null);
  const sdkInstanceRef = useRef(null);
  const tokenizeTimerRef = useRef(null);
  const [ready, setReady] = useState(false);
  const [isCardValid, setIsCardValid] = useState(false);
  const [tokenizing, setTokenizing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [tapConfig, setTapConfig] = useState(null);
  const [cardholderName, setCardholderName] = useState("");
  const [phase, setPhase] = useState("idle"); // idle | tokenizing | authorizing | redirecting | success | error

  const t = (ar, en) => (lang === "ar" ? ar : en);
  const price = plan?.prices?.[cycle];
  const perLabel = cycle === "monthly" ? t("شهريًا", "per month") : t("سنويًا", "per year");
  const name = lang === "ar" ? (plan?.name_ar || plan?.name) : plan?.name;

  // Load Tap public config once
  useEffect(() => {
    let mounted = true;
    if (!open) return;
    setError("");
    setPhase("idle");
    (async () => {
      try {
        const r = await api.get("/billing/tap/config");
        if (mounted) setTapConfig(r.data);
      } catch (e) {
        if (mounted) setError(t("تعذّر تحميل إعدادات الدفع", "Failed to load payment config"));
      }
    })();
    return () => { mounted = false; };
  }, [open]);

  // Render Tap Card SDK when config + DOM are ready
  useEffect(() => {
    if (!open || !tapConfig || !containerRef.current) return;

    let cancelled = false;

    const waitForSDK = (retries = 40) => new Promise((resolve, reject) => {
      const check = () => {
        if (window.CardSDK && typeof window.CardSDK.renderTapCard === "function") return resolve(window.CardSDK);
        if (retries-- <= 0) return reject(new Error("SDK_TIMEOUT"));
        setTimeout(check, 150);
      };
      check();
    });

    (async () => {
      try {
        const sdk = await waitForSDK();
        if (cancelled) return;
        // Clear previous instance if any
        if (containerRef.current) containerRef.current.innerHTML = "";
        // Enum constants exposed by the V2 SDK — use these instead of raw strings.
        // Tap's live-mode iframe validates strictly against these; sending raw
        // strings works in test-mode only and produces HTTP 400 on live.
        const { Theme, Currencies, Direction, Edges, Locale } = sdk;
        const instance = sdk.renderTapCard("tap-card-container", {
          publicKey: tapConfig.public_key,
          merchant: { id: tapConfig.merchant_id },
          transaction: { amount: 1, currency: Currencies.SAR },
          customer: {
            name: [
              {
                lang: lang === "ar" ? Locale.AR : Locale.EN,
                first: cardholderName || "DAAM",
                last: "Customer",
                middle: "",
              },
            ],
            nameOnCard: cardholderName || "",
            editable: true,
            contact: {
              email: "",
              phone: { countryCode: "966", number: "" },
            },
          },
          acceptance: {
            supportedBrands: ["VISA", "MASTERCARD", "MADA", "AMERICAN_EXPRESS"],
            supportedCards: "ALL",
          },
          // Option A (Feb 2026): removed `cardHolder: true` because the
          // Tap iframe was setting `collectHolderName: true` internally
          // WITHOUT rendering a visible cardholder input, then rejecting
          // tokenize with a required-field error when `preLoadCardName`
          // was empty. Our external optional cardholder input is
          // unaffected; if the user types a name, it is still passed
          // through via `customer.nameOnCard` above and echoed on the
          // Tap customer record.
          fields: {},
          addons: { displayPaymentBrands: true, loader: true, saveCard: true },
          interface: {
            locale: lang === "ar" ? Locale.AR : Locale.EN,
            theme: Theme.LIGHT,
            edges: Edges.CURVED,
            direction: lang === "ar" ? Direction.RTL : Direction.LTR,
          },
          onReady: () => {
            if (!cancelled) setReady(true);
            postDiagnostic("onReady", {
              plan_id: plan?.id,
              cycle,
              lang,
              hostname: (typeof window !== "undefined" && window.location) ? window.location.hostname : "",
            });
            // Preview-only diagnostic — never logs on production (getdaam.com).
            if (typeof window !== "undefined" && window.location && window.location.hostname !== "getdaam.com") {
              console.log("[TapCardModal] onReady");
            }
          },
          onFocus: () => setError(""),
          onValidInput: (state) => {
            if (cancelled) return;
            const valid = typeof state === "boolean" ? state : !!state?.isValid;
            setIsCardValid(valid);
            if (typeof window !== "undefined" && window.location && window.location.hostname !== "getdaam.com") {
              // Only booleans logged — no card number, CVV, name, or token.
              console.log("[TapCardModal] onValidInput  arg=", state, " → isCardValid:=", valid);
            }
          },
          onInvalidInput: (state) => {
            if (cancelled) return;
            // Fire telemetry for the FIRST invalid signal per mount so we
            // can see which field the SDK is unhappy with. State is
            // sanitized by postDiagnostic before it leaves the browser.
            postDiagnostic("onInvalidInput", { state_shape: state });
            if (typeof window !== "undefined" && window.location && window.location.hostname !== "getdaam.com") {
              console.log("[TapCardModal] onInvalidInput arg=", state, "  (see notes on double-invocation)");
            }
            // Note: Tap V2 SDK invokes onInvalidInput on every state change with
            // the NEGATION of the validity flag (see /tmp/tap_sdk.js inline:
            // `e.onValidInput(r); e.onInvalidInput(!r);`). Ignoring the arg and
            // unconditionally setting `false` here is the source of the
            // permanently-disabled button reported Feb 2026. `onValidInput`
            // above is sufficient on its own; we no longer touch state here.
          },
          onBinIdentification: () => {},
          onChangeSaveCardLater: () => {},
          onSuccess: (raw) => {
            if (tokenizeTimerRef.current) { clearTimeout(tokenizeTimerRef.current); tokenizeTimerRef.current = null; }
            // Production-safe telemetry: SHAPE ONLY. postDiagnostic recursively
            // strips card / cvv / token / name / email / phone keys before
            // anything is transmitted.
            const parsedForShape = (() => {
              try { return typeof raw === "string" ? JSON.parse(raw) : raw; }
              catch { return { __parse_error__: true }; }
            })();
            postDiagnostic("onSuccess", {
              raw_type: typeof raw,
              payload_shape: parsedForShape,
            });
            // -----------------------------------------------------------
            // Preview-only diagnostic (hostname-gated — never runs on
            // getdaam.com). Logs SHAPE only: top-level key names, nested
            // key names, and value TYPES. Never logs the token string,
            // the card number, the CVV, the expiry, or the cardholder.
            // -----------------------------------------------------------
            if (typeof window !== "undefined" && window.location && window.location.hostname !== "getdaam.com") {
              const previewShape = (() => {
                try { return typeof raw === "string" ? JSON.parse(raw) : raw; }
                catch { return raw; }
              })();
              const shape = (v) => {
                if (v === null) return "null";
                if (v === undefined) return "undefined";
                if (Array.isArray(v)) return `array[len=${v.length}]`;
                if (typeof v === "object") {
                  const out = {};
                  for (const k of Object.keys(v)) {
                    const cv = v[k];
                    if (cv !== null && typeof cv === "object") {
                      // Recurse one level so we can see nested id/token/card locations.
                      out[k] = shape(cv);
                    } else {
                      out[k] = typeof cv;   // types only, no values
                    }
                  }
                  return out;
                }
                return typeof v;
              };
              console.log("[TapCardModal] onSuccess payload shape:", JSON.stringify(shape(previewShape), null, 2));
            }
            try {
              const parsed = typeof raw === "string" ? JSON.parse(raw) : raw;
              handleTokenized(parsed);
            } catch (e) {
              setError(t("رمز غير صالح من بوابة الدفع", "Invalid tokenization response"));
              setPhase("error");
              setTokenizing(false);
              setSubmitting(false);
            }
          },
          onError: (err) => {
            if (tokenizeTimerRef.current) { clearTimeout(tokenizeTimerRef.current); tokenizeTimerRef.current = null; }
            // Production-safe telemetry: full sanitized error shape so we
            // can see Tap's error code / description / missing field name.
            postDiagnostic("onError", {
              err_type: typeof err,
              err_shape: err,
              // Explicit allowlisted primitives — kept verbatim by sanitize().
              code: err?.code || err?.error_code,
              description: err?.description,
              message: err?.message,
              reason: err?.reason,
              field: err?.field || err?.field_name || err?.missing_field,
            });
            if (typeof window !== "undefined" && window.location && window.location.hostname !== "getdaam.com") {
              console.log("[TapCardModal] onError", err);
            }
            const msg = err?.description || err?.message || t("تعذّر التحقق من البطاقة", "Card validation failed");
            setError(msg);
            setPhase("error");
            setTokenizing(false);
            setSubmitting(false);
          },
        });
        sdkInstanceRef.current = instance;
      } catch (e) {
        if (!cancelled) setError(t("تعذّر تحميل نموذج البطاقة", "Failed to load card form"));
      }
    })();

    return () => {
      cancelled = true;
      setReady(false);
      setIsCardValid(false);
      if (tokenizeTimerRef.current) { clearTimeout(tokenizeTimerRef.current); tokenizeTimerRef.current = null; }
      sdkInstanceRef.current = null;
    };
  }, [open, tapConfig, lang]);

  const handleTokenized = async (token) => {
    // Defensive token-id extraction (Feb 2026): see /src/lib/tapToken.js
    // for the full explanation. Handles direct, nested, and card-shaped
    // payloads across card brands / browsers.
    const tokenId = extractTokenId(token);

    // Preview-only diagnostic — confirms exactly WHERE the token id was
    // found (or that it wasn't found). Never logs the string.
    const tokenLocation =
      (token && typeof token.id === "string") ? "top-level .id"
      : (token && token.token && typeof token.token.id === "string") ? ".token.id"
      : (token && token.card && typeof token.card.id === "string") ? ".card.id"
      : "NOT FOUND";
    postDiagnostic("extractTokenId", {
      found: !!tokenId,
      length: tokenId ? tokenId.length : 0,
      location: tokenLocation,
    });
    if (typeof window !== "undefined" && window.location && window.location.hostname !== "getdaam.com") {
      console.log(
        "[TapCardModal] extractTokenId →",
        tokenId ? `non-empty string (len=${tokenId.length}, location=${tokenLocation})` : "null (no usable token id)",
      );
    }

    if (!tokenId) {
      postDiagnostic("start-trial.skipped", { reason: "no_token_id" });
      // No usable token — do NOT call the backend. Show a safe bilingual
      // message and stay in the modal so the user can retry without
      // triggering an ErrorBoundary crash.
      setError(t(
        "تعذّر التحقق من بيانات البطاقة. يُرجى إدخال بيانات البطاقة مجدداً.",
        "We couldn't verify your card details. Please re-enter the card fields.",
      ));
      setPhase("error");
      setSubmitting(false);
      setTokenizing(false);
      return;
    }

    setPhase("authorizing");
    setSubmitting(true);
    setError("");
    try {
      const requestBody = {
        plan_id: plan.id,
        billing_cycle: cycle,
        token_id: tokenId,
        cardholder_name: cardholderName || undefined,
      };
      // Preview-only diagnostic — logs the KEY NAMES + TYPES only. Never
      // logs the token_id value or the cardholder name value.
      if (typeof window !== "undefined" && window.location && window.location.hostname !== "getdaam.com") {
        const shape = {};
        for (const k of Object.keys(requestBody)) {
          const v = requestBody[k];
          shape[k] = v === undefined ? "undefined" : `${typeof v}(non-empty=${v !== "" && v != null})`;
        }
        console.log("[TapCardModal] POST /billing/tap/start-trial body shape:", shape);
      }
      const r = await api.post("/billing/tap/start-trial", requestBody);
      const data = r.data || {};
      postDiagnostic("start-trial.ok", {
        requires_3ds: !!data.requires_3ds,
        has_redirect: !!data.redirect_url,
        response_shape: data,
      });
      if (data.requires_3ds && data.redirect_url) {
        setPhase("redirecting");
        // Give the user a beat to see the message, then redirect
        setTimeout(() => { window.location.href = data.redirect_url; }, 400);
        return;
      }
      setPhase("success");
      onSuccess && onSuccess(data);
    } catch (e) {
      // Sanitized 422/4xx telemetry — surfaces the exact missing field on prod.
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      let sanitisedDetail = detail;
      if (Array.isArray(detail)) {
        sanitisedDetail = detail.map((row) => ({
          type: row?.type,
          loc: row?.loc,
          msg: row?.msg,
        }));
      }
      postDiagnostic("start-trial.error", {
        http_status: status,
        detail_shape: sanitisedDetail,
      });
      // Preview-only console mirror (never runs on getdaam.com).
      if (typeof window !== "undefined" && window.location && window.location.hostname !== "getdaam.com") {
        console.log("[TapCardModal] POST /start-trial error status=", status, " detail=", JSON.stringify(sanitisedDetail));
      }
      // ALWAYS render a string. `formatApiError` safely folds Pydantic
      // arrays and dict-shaped 402 detail objects (e.g. AI-cost-cap
      // errors with message_ar/message_en) into a single locale-aware
      // string. Passing a non-string here previously crashed React.
      setError(formatApiError(e));
      setPhase("error");
    } finally {
      setSubmitting(false);
      setTokenizing(false);
    }
  };

  const triggerSubmit = () => {
    if (!sdkInstanceRef.current && !window.CardSDK) return;
    if (!isCardValid) {
      // Guard: never call tokenize() until the SDK reports valid fields — otherwise
      // the SDK silently ignores the postMessage and the UI hangs.
      setError(t(
        "يرجى إدخال رقم بطاقة صالح مع تاريخ الانتهاء ورمز CVV.",
        "Please enter a valid card number, expiry, and CVV before continuing."
      ));
      return;
    }
    setTokenizing(true);
    setPhase("tokenizing");
    setError("");

    // Safety net: if the SDK doesn't call onSuccess/onError within 20s,
    // recover the UI so the user isn't stuck on "Verifying card...".
    if (tokenizeTimerRef.current) clearTimeout(tokenizeTimerRef.current);
    tokenizeTimerRef.current = setTimeout(() => {
      tokenizeTimerRef.current = null;
      postDiagnostic("tokenize.timeout", { elapsed_ms: TOKENIZE_TIMEOUT_MS });
      setError(t(
        "لم تستجب بوابة الدفع خلال الوقت المتوقع. تحقق من اتصالك بالإنترنت وحاول مرة أخرى.",
        "The payment gateway didn't respond in time. Check your connection and try again."
      ));
      setPhase("error");
      setTokenizing(false);
      setSubmitting(false);
    }, TOKENIZE_TIMEOUT_MS);

    // The SDK v2 exposes tokenize() as a global function that postMessages the iframe.
    // Prefer the global explicitly — instance.tokenize does not exist on v2.
    let tokenizePath = "none";
    try {
      if (window.CardSDK && typeof window.CardSDK.tokenize === "function") {
        tokenizePath = "window.CardSDK.tokenize";
        postDiagnostic("tokenize.invoke", { path: tokenizePath });
        window.CardSDK.tokenize();
        return;
      }
      // Legacy fallbacks (older SDK versions)
      const inst = sdkInstanceRef.current;
      if (inst && typeof inst.tokenize === "function") {
        tokenizePath = "instance.tokenize";
        postDiagnostic("tokenize.invoke", { path: tokenizePath });
        return inst.tokenize();
      }
      if (inst && typeof inst.submit === "function") {
        tokenizePath = "instance.submit";
        postDiagnostic("tokenize.invoke", { path: tokenizePath });
        return inst.submit();
      }
      throw new Error("SDK_NO_TOKENIZE_METHOD");
    } catch (e) {
      if (tokenizeTimerRef.current) { clearTimeout(tokenizeTimerRef.current); tokenizeTimerRef.current = null; }
      postDiagnostic("tokenize.throw", { path: tokenizePath, message: e?.message });
      setError(e?.message || t("تعذّر إرسال البطاقة", "Failed to submit card"));
      setPhase("error");
      setTokenizing(false);
    }
  };

  if (!open || !plan) return null;

  const isBusy = tokenizing || submitting || phase === "redirecting";

  return (
    <div
      data-testid="tap-card-modal"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm animate-fade-in-up p-4"
      onClick={(e) => { if (e.target === e.currentTarget && !isBusy) onClose(); }}
    >
      <div className="nx-card p-0 w-full max-w-lg overflow-hidden" dir={lang === "ar" ? "rtl" : "ltr"}>
        {/* Header */}
        <div className="p-6 border-b border-border relative">
          {!isBusy && (
            <button
              data-testid="tap-modal-close"
              onClick={onClose}
              className="absolute top-4 end-4 text-muted-foreground hover:text-foreground"
            >
              <X className="w-4 h-4" />
            </button>
          )}
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-sm bg-[#B8875D]/10 text-[#B8875D] flex items-center justify-center">
              <ShieldCheck className="w-5 h-5" />
            </div>
            <div>
              <h3 className="font-display text-xl leading-tight">{t("تأكيد بطاقتك لبدء التجربة", "Verify your card to start the trial")}</h3>
              <p className="text-xs text-muted-foreground mt-1">
                {t(
                  `خطة ${name} — ${price} ${plan.currency || "SAR"} ${perLabel} · ٧ أيام تجربة مجانية`,
                  `${name} plan — ${price} ${plan.currency || "SAR"} ${perLabel} · 7-day free trial`
                )}
              </p>
            </div>
          </div>
        </div>

        {/* Body */}
        {phase !== "success" && (
          <div className="p-6 space-y-4">
            <div className="p-3 rounded-sm bg-primary/5 border border-primary/20 text-xs leading-relaxed">
              {t(
                "سنقوم بالتحقق من بطاقتك عبر تفويض مؤقت بقيمة 1 ريال سعودي (يتم إلغاؤه خلال ساعة). لن يتم أي خصم فعلي حتى انتهاء تجربتك المجانية بعد ٧ أيام. يمكنك الإلغاء في أي وقت من صفحة الفواتير.",
                "We will verify your card with a temporary SAR 1 authorization (auto-voided within 1 hour). No actual charge occurs until your 7-day free trial ends. You can cancel anytime from the Billing page."
              )}
            </div>

            <div>
              <label className="text-xs text-muted-foreground block mb-1">{t("اسم حامل البطاقة (اختياري)", "Cardholder name (optional)")}</label>
              <input
                data-testid="tap-cardholder-name"
                type="text"
                value={cardholderName}
                onChange={(e) => setCardholderName(e.target.value)}
                disabled={isBusy}
                placeholder={t("كما هو موضح على البطاقة", "As shown on card")}
                className="w-full px-3 py-2 rounded-sm border border-border bg-background text-sm"
              />
            </div>

            {/* Tap Card SDK mount point */}
            <div>
              <div
                id="tap-card-container"
                ref={containerRef}
                data-testid="tap-card-container"
                className="min-h-[220px] rounded-sm border border-border bg-background"
              />
              {!ready && !error && (
                <div className="text-xs text-muted-foreground mt-2 flex items-center gap-2">
                  <Loader2 className="w-3 h-3 animate-spin" />
                  {t("جاري تحميل نموذج الدفع الآمن...", "Loading secure payment form...")}
                </div>
              )}
            </div>

            {error && (
              <div data-testid="tap-modal-error" className="p-3 rounded-sm bg-destructive/10 border border-destructive/30 text-xs text-destructive flex items-start gap-2">
                <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
                <span>{error}</span>
              </div>
            )}

            <div className="flex items-center justify-between text-[10px] text-muted-foreground">
              <span className="flex items-center gap-1">
                <Lock className="w-3 h-3" />
                {t("مدفوعات مشفّرة بواسطة Tap Payments", "Payments encrypted by Tap Payments")}
              </span>
              <span>{t("مدى · فيزا · ماستركارد", "MADA · Visa · Mastercard")}</span>
            </div>

            <button
              data-testid="tap-modal-submit"
              onClick={triggerSubmit}
              disabled={!ready || isBusy || !isCardValid}
              title={!isCardValid && ready && !isBusy ? t("أكمل بيانات البطاقة أولاً", "Enter valid card details first") : undefined}
              className={`nx-btn-accent w-full flex items-center justify-center gap-2 ${(!ready || isBusy || !isCardValid) ? "opacity-50 cursor-not-allowed" : ""}`}
            >
              {isBusy && <Loader2 className="w-4 h-4 animate-spin" />}
              {phase === "tokenizing" && t("جاري التحقق من البطاقة...", "Verifying card...")}
              {phase === "authorizing" && t("جاري إعداد التجربة...", "Preparing trial...")}
              {phase === "redirecting" && t("جاري التوجيه للمصادقة الآمنة...", "Redirecting to secure 3DS...")}
              {(phase === "idle" || phase === "error") && (isCardValid
                ? t("تأكيد وبدء التجربة", "Confirm & start trial")
                : t("أكمل بيانات البطاقة", "Complete card details"))}
            </button>
          </div>
        )}

        {phase === "success" && (
          <div className="p-8 text-center" data-testid="tap-modal-success">
            <div className="w-16 h-16 rounded-sm bg-emerald-500/10 text-emerald-600 flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 className="w-8 h-8" />
            </div>
            <h3 className="font-display text-2xl mb-2">{t("تم تفعيل تجربتك", "Trial activated")}</h3>
            <p className="text-sm text-muted-foreground leading-relaxed mb-6">
              {t(
                `تجربة ${name} المجانية لمدة ٧ أيام قيد التشغيل. ستتم محاولة أول خصم تلقائيًا عند انتهاء التجربة، ويمكنك الإلغاء في أي وقت.`,
                `Your ${name} 7-day free trial is now running. The first charge will be attempted automatically at the end of the trial — cancel anytime.`
              )}
            </p>
            <button data-testid="tap-modal-done" onClick={onClose} className="nx-btn-primary w-full">
              {t("تم", "Done")}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
